/**
 * /cancellation — a PAGE. AppLayoutRoute supplies the chrome.
 *
 * Real policy. The substance of it is the handover: what we give back, in what
 * state, and how long you have to collect it. A managed-infrastructure provider
 * that is vague here is telling you something.
 */
import { LegalPage } from '../components/LegalPage';

export default function CancellationPage() {
  return (
    <LegalPage
      title="Cancellation policy"
      description="How to cancel Bloomneo: 30 days' notice, everything kept running through it, and domains, servers, databases and backups handed back to you."
      updated="20 September 2026"
      draft={false}
      intro={<>Nothing here is designed to make leaving expensive or slow. The notice period exists so the handover is orderly, not so the relationship is.</>}
      sections={[
        {
          heading: 'How to cancel',
          body: (
            <p>
              Email{' '}
              <a href="mailto:support@bloomneo.com" className="text-primary underline-offset-4 hover:underline">support@bloomneo.com</a>{' '}
              from an address on the account. We acknowledge within two working days and confirm
              the date the 30 days' notice ends. You do not have to give a reason, though we will
              probably ask, and you are free to decline.
            </p>
          ),
        },
        {
          heading: 'What happens during the notice period',
          body: (
            <>
              <p>
                Everything keeps running exactly as before: patching, certificate renewals, nightly
                backups, monitoring and alerts. A service winding down is the worst possible time
                to stop watching it.
              </p>
              <p className="mt-3">
                We use the period to hand over rather than to wait it out — transferring DNS,
                giving you server access, and walking whoever takes over through how it is
                arranged.
              </p>
            </>
          ),
        },
        {
          heading: 'What you get back',
          body: (
            <ul className="list-disc space-y-1.5 pl-5">
              <li>Domains, which were registered to you throughout, with registrar access.</li>
              <li>Root access to the servers, or assistance moving to infrastructure of your own.</li>
              <li>A final database dump, plus the 30 days of nightly backups we hold.</li>
              <li>Files from object storage, and the credentials to the bucket if it is yours.</li>
              <li>DNS zone records, so nothing has to be retyped from a screenshot.</li>
            </ul>
          ),
        },
        {
          heading: 'What happens to your data afterwards',
          body: (
            <p>
              Backups age out on their normal 30-day cycle after the last one is taken, and we
              remove account details within 30 days of the account closing, apart from what we
              have to keep for tax or legal reasons. If you want everything deleted sooner, say so
              and we will do it once you confirm you have what you need.
            </p>
          ),
        },
        {
          heading: 'Final invoice',
          body: (
            <p>
              The last invoice covers the notice period and any pass-through costs incurred during
              it. Nothing is charged after the notice period ends. See the{' '}
              <a href="/refund" className="text-primary underline-offset-4 hover:underline">refund policy</a>{' '}
              for what is refundable.
            </p>
          ),
        },
        {
          heading: 'If we cancel',
          body: (
            <p>
              We give the same 30 days' notice, and the same handover. The exceptions are
              non-payment and misuse of the infrastructure, which are covered in the{' '}
              <a href="/terms" className="text-primary underline-offset-4 hover:underline">terms</a>.
            </p>
          ),
        },
      ]}
    />
  );
}
